import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DollarSign, CreditCard, CheckCircle2, Clock, Loader2, AlertCircle,
  ShieldCheck, XCircle, Lock, ExternalLink, Settings, AlertTriangle, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  pending: { label: 'Pending', color: 'bg-amber-600', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-600', icon: CheckCircle2 },
  waived: { label: 'Waived', color: 'bg-blue-600', icon: ShieldCheck },
  failed: { label: 'Failed', color: 'bg-red-600', icon: XCircle },
};

export default function PaymentPanel({ orgId, isAdmin = false }) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(null);
  const [paySuccess, setPaySuccess] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({
    multi_player_discount_pct: 10,
    coach_parent_discount_pct: 0,
    payment_schedule: { enabled: false, installments: 1, interval_days: 30 }
  });
  const [overrideReason, setOverrideReason] = useState('');

  const fetchPayments = useCallback(async () => {
    try {
      if (isAdmin && orgId) {
        const { data } = await axios.get(`${API_URL}/api/payments/org/${orgId}`);
        setPayments(data.payments || []);
        setSummary(data.summary || null);
      } else {
        const { data } = await axios.get(`${API_URL}/api/payments/family`);
        setPayments(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, isAdmin]);

  const fetchConfig = useCallback(async () => {
    if (!isAdmin || !orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/config/schedule/${orgId}`);
      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch payment config:', err);
    }
  }, [orgId, isAdmin]);

  useEffect(() => { fetchPayments(); fetchConfig(); }, [fetchPayments, fetchConfig]);

  // Check for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please refresh to check status.');
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/checkout-status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setPaySuccess(true);
        setTimeout(() => setPaySuccess(null), 5000);
        fetchPayments();
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error('Status poll error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    }
  };

  const handlePay = async (paymentId, installmentNumber = null) => {
    setPaying(paymentId);
    setError('');
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(`${API_URL}/api/payments/${paymentId}/pay`, {
        origin_url: originUrl,
        installment_number: installmentNumber
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment initiation failed');
      setPaying(null);
    }
  };

  const handleOverride = async (paymentId, overrideType) => {
    setPaying(paymentId);
    try {
      await axios.post(`${API_URL}/api/payments/${paymentId}/override`, {
        override_type: overrideType,
        reason: overrideReason || `${overrideType} - approved by AD`
      });
      setOverrideReason('');
      fetchPayments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Override failed');
    } finally {
      setPaying(null);
    }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/payments/config/schedule/${orgId}`, config);
      setShowConfig(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Config save failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="space-y-4">
      {!isAdmin && <FamilyInvoices isDark={isDark} onPay={handlePay} />}
      {/* Summary Cards (AD view) */}
      {isAdmin && summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Revenue', value: `$${summary.total_revenue}`, color: 'text-green-400' },
            { label: 'Pending', value: `$${summary.pending_amount}`, color: 'text-amber-400' },
            { label: 'Paid', value: summary.paid_count, color: 'text-green-400' },
            { label: 'Outstanding', value: summary.pending_count, color: 'text-amber-400' },
            { label: 'Overdue', value: summary.overdue_count || 0, color: summary.overdue_count > 0 ? 'text-red-400' : 'text-slate-400' },
          ].map((s) => (
            <Card key={s.label} className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Overdue Alerts */}
      {isAdmin && summary?.overdue?.length > 0 && (
        <Card className="border-red-500/50 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span className={`font-medium text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  Overdue Payments ({summary.overdue.length})
                </span>
              </div>
              <Button size="sm" variant="outline" className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="send-reminders-btn"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API_URL}/api/payments/send-reminders/${orgId}`, {}, { withCredentials: true });
                    alert(`Sent ${res.data.sent_count} payment reminder(s)`);
                  } catch (err) { console.error(err); }
                }}
              >
                <Mail className="h-3 w-3 mr-1" /> Send Reminders
              </Button>
            </div>
            {summary.overdue.slice(0, 3).map((o, i) => (
              <div key={i} className={`text-xs ${isDark ? 'text-red-300' : 'text-red-500'}`}>
                Installment #{o.installment} - ${o.amount} due {new Date(o.due_date).toLocaleDateString()}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <DollarSign className="h-5 w-5 text-green-500" />
            {isAdmin ? 'Payment Dashboard' : 'My Payments'}
          </CardTitle>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setShowConfig(true)} className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="payment-config-btn">
              <Settings className="h-3 w-3 mr-1" />Config
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400 mb-4" data-testid="payment-error">
              <AlertCircle className="h-4 w-4" /><span className="text-sm">{error}</span>
            </div>
          )}

          {payments.length === 0 ? (
            <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <CreditCard className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No payments yet</p>
              <p className="text-sm">Payments will appear after registration checkout</p>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => {
                const st = STATUS_MAP[payment.status] || STATUS_MAP.pending;
                const Icon = st.icon;
                return (
                  <div key={payment.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`payment-${payment.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={st.color}><Icon className="h-3 w-3 mr-1" />{st.label}</Badge>
                          <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>${payment.total}</span>
                        </div>
                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {new Date(payment.created_at).toLocaleDateString()}
                          {payment.paid_at && ` · Paid ${new Date(payment.paid_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {['pending', 'partial', 'cash_pending'].includes(payment.status) && !isAdmin && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setShowConfirm(payment)} data-testid={`pay-btn-${payment.id}`}>
                            <CreditCard className="h-3 w-3 mr-1" />Pay Now
                          </Button>
                        )}
                        {payment.status === 'pending' && isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => handleOverride(payment.id, 'scholarship')} disabled={paying === payment.id} className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid={`override-btn-${payment.id}`}>
                            {paying === payment.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Waive'}
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Items */}
                    {payment.items?.map((item, i) => (
                      <div key={i} className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {item.player_name || item.description} · ${item.amount}
                      </div>
                    ))}
                    {payment.discounts?.length > 0 && (
                      <div className="text-xs text-green-400 mt-1">{payment.discounts[0].description}</div>
                    )}
                    {/* Installments */}
                    {payment.installments?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Payment Schedule:</p>
                        {payment.installments.map((inst, i) => (
                          <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${inst.status === 'paid' ? 'bg-green-500/10 text-green-400' : inst.due_date < new Date().toISOString() ? 'bg-red-500/10 text-red-400' : isDark ? 'bg-slate-600/30 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                            <span>#{inst.number} - ${inst.amount}</span>
                            <span>Due {new Date(inst.due_date).toLocaleDateString()}</span>
                            <span className="flex items-center gap-1">
                              <Badge className={inst.status === 'paid' ? 'bg-green-600' : 'bg-amber-600'}>{inst.status}</Badge>
                              {!isAdmin && ['pending', 'partial'].includes(inst.status) && ['pending', 'partial', 'cash_pending'].includes(payment.status) && (
                                <button className="underline" disabled={paying === payment.id} onClick={() => handlePay(payment.id, inst.number)} data-testid={`pay-inst-${payment.id}-${inst.number}`}>Pay</button>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <a href={`${API_URL}/api/payments/${payment.id}/receipt`} target="_blank" rel="noreferrer" className="text-xs underline text-amber-500 mt-1 inline-block" data-testid={`receipt-${payment.id}`}>Receipt</a>
                    {payment.override && (
                      <div className="text-xs text-blue-400 mt-1">Override: {payment.override.type} - {payment.override.reason}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Success Toast */}
      {paySuccess && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in slide-in-from-bottom" data-testid="payment-success-toast">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-medium">Payment Successful!</span>
        </div>
      )}

      {/* Payment Confirmation Dialog */}
      <Dialog open={!!showConfirm} onOpenChange={() => setShowConfirm(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <CreditCard className="h-5 w-5 text-green-500" />
              Confirm Payment
            </DialogTitle>
          </DialogHeader>
          {showConfirm && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className="flex justify-between mb-2">
                  <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Subtotal</span>
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>${showConfirm.subtotal}</span>
                </div>
                {showConfirm.discounts?.map((d, i) => (
                  <div key={i} className="flex justify-between text-green-500 text-sm">
                    <span>{d.description}</span>
                    <span>-${d.amount}</span>
                  </div>
                ))}
                <div className={`flex justify-between pt-2 mt-2 border-t font-bold ${isDark ? 'border-slate-600 text-white' : 'border-slate-200 text-slate-900'}`}>
                  <span>Total</span>
                  <span className="text-lg">${showConfirm.total}</span>
                </div>
              </div>

              <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4 text-green-500" />
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Secure payment via Stripe</span>
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  You'll be redirected to Stripe's secure checkout page to complete your payment.
                </p>
              </div>

              <div className={`text-sm space-y-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {showConfirm.items?.map((item, i) => (
                  <div key={i}>{item.player_name} - ${item.amount}</div>
                ))}
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                  <AlertCircle className="h-4 w-4" /><span className="text-sm">{error}</span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowConfirm(null)}>Cancel</Button>
                {showConfirm.installments?.length > 0 ? (
                  showConfirm.installments.filter(i => i.status === 'pending').map((inst) => (
                    <Button key={inst.number} className="bg-green-600 hover:bg-green-700" onClick={() => handlePay(showConfirm.id, inst.number)} disabled={paying === showConfirm.id} data-testid={`pay-installment-${inst.number}`}>
                      {paying === showConfirm.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                      Pay #{inst.number} (${inst.amount})
                    </Button>
                  ))
                ) : (
                  <Button className="bg-green-600 hover:bg-green-700" onClick={() => handlePay(showConfirm.id)} disabled={paying === showConfirm.id} data-testid="confirm-pay-btn">
                    {paying === showConfirm.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                    Pay ${showConfirm.total}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Config Dialog (AD) */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Settings className="h-5 w-5 text-blue-500" />Payment Configuration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Multi-player Discount (%)</Label>
              <Input type="number" min="0" max="100" value={config.multi_player_discount_pct} onChange={(e) => setConfig({...config, multi_player_discount_pct: parseInt(e.target.value) || 0})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="config-multi-discount" />
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Discount for families registering 2+ players</p>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Coach Parent Discount (%)</Label>
              <Input type="number" min="0" max="100" value={config.coach_parent_discount_pct} onChange={(e) => setConfig({...config, coach_parent_discount_pct: parseInt(e.target.value) || 0})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="config-coach-discount" />
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Discount if parent is a coach in the organization</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={config.payment_schedule?.enabled || false} onChange={(e) => setConfig({...config, payment_schedule: {...(config.payment_schedule || {}), enabled: e.target.checked}})} id="enable_schedule" />
                <Label htmlFor="enable_schedule" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Enable Payment Installments</Label>
              </div>
              {config.payment_schedule?.enabled && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Number of installments</Label>
                    <Input type="number" min="2" max="12" value={config.payment_schedule?.installments || 2} onChange={(e) => setConfig({...config, payment_schedule: {...config.payment_schedule, installments: parseInt(e.target.value) || 2}})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="config-installments" />
                  </div>
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Days between payments</Label>
                    <Input type="number" min="7" max="90" value={config.payment_schedule?.interval_days || 30} onChange={(e) => setConfig({...config, payment_schedule: {...config.payment_schedule, interval_days: parseInt(e.target.value) || 30}})} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="config-interval" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setShowConfig(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveConfig} data-testid="save-config-btn">Save Configuration</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// FA1 / I6 / I7 — the family's invoices: total / paid / balance, every payment
// (cash and check ones included), pay the balance or only what is past due, and a
// printable receipt. Card payments go through the club's payment processor.
function FamilyInvoices({ isDark, onPay }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/invoicing/my`).then(r => setRows(r.data.invoices || [])).catch(() => setRows([]));
  }, []);
  if (!rows.length) return null;
  const pay = async (inv, mode) => {
    setBusy(inv.id + mode);
    try {
      const { data } = await axios.post(`${API_URL}/api/invoicing/my/${inv.id}/pay`, { mode });
      await onPay(data.payment_id);
    } catch (e) { alert(e.response?.data?.detail || 'Could not start payment'); }
    finally { setBusy(null); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} data-testid="family-invoices">
      <CardHeader><CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>My invoices</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {rows.map(inv => {
          const pastDue = (inv.installments || []).some(i => i.status !== 'paid' && String(i.due_date).slice(0, 10) <= today);
          return (
            <div key={inv.id} className={`p-3 rounded-lg border ${isDark ? 'border-slate-700' : 'border-slate-200'}`} data-testid={`my-invoice-${inv.id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{inv.title} <span className="text-xs text-slate-400">{inv.invoice_number} · {inv.org_name}</span></div>
                  <div className="text-xs text-slate-400">Total ${Number(inv.total).toFixed(2)} · Paid ${Number(inv.amount_paid).toFixed(2)} · <b>Balance ${Number(inv.amount_due).toFixed(2)}</b>{inv.due_date ? ` · Due ${inv.due_date}` : ''}</div>
                </div>
                <div className="flex gap-2 items-center">
                  {inv.amount_due > 0 && pastDue && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => pay(inv, 'past_due')} data-testid={`pay-pastdue-${inv.id}`}>Pay past due</Button>}
                  {inv.amount_due > 0 && <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={!!busy} onClick={() => pay(inv, 'balance')} data-testid={`pay-balance-${inv.id}`}><CreditCard className="h-3 w-3 mr-1" />Pay balance</Button>}
                  <a className="text-xs underline text-amber-500" href={`${API_URL}/api/invoicing/my/${inv.id}/receipt`} target="_blank" rel="noreferrer">Receipt</a>
                </div>
              </div>
              {(inv.installments || []).length > 0 && (
                <div className="mt-2 text-xs space-y-0.5">{inv.installments.map(i => <div key={i.number} className="flex justify-between"><span>#{i.number} due {i.due_date}</span><span>${Number(i.amount).toFixed(2)} · {i.status}</span></div>)}</div>
              )}
              {(inv.payments || []).length > 0 && (
                <div className="mt-2 text-xs text-slate-400">Payments: {inv.payments.map(p => `${p.date} ${p.method} $${Number(p.amount).toFixed(2)}`).join(' · ')}</div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

